<template>
  <a-button
    v-bind="buttonProps"
    :class="[
      'button',
      `button--type-${type}`,
      `button--size-${size}`,
      `button--shape-${shape}`,
      status !== 'normal' ? `button--status-${status}` : '',
      { 'button--long': long },
      loading ? `button--loading-${type}` : '',
      loading && status !== 'normal' ? `button--loading-status-${status}` : '',
      loading && status === 'normal' && type === 'primary' ? 'button--loading-status-normal' : '',
      { 'button--loading': loading },
      customClass,
    ]"
    @click="handleClick"
  >
    <!-- 前置图标：使用 a-button 默认的 icon 插槽 -->
    <template v-if="$slots.icon || $slots.prefix" #icon>
      <slot name="prefix">
        <slot name="icon" />
      </slot>
    </template>
    <span class="button__content">
      <slot />
      <span v-if="$slots.suffix" class="button__suffix">
        <slot name="suffix" />
      </span>
    </span>
  </a-button>
</template>

<script setup>
/**
 * @description 通用按钮组件，封装统一的按钮样式
 * @param {string} type - Arco Design 按钮类型：'default' | 'primary' | 'secondary' | 'dashed' | 'outline'，默认为 'primary'
 * @param {string} size - Arco Design 按钮尺寸：'mini' | 'small' | 'medium' | 'large'，默认为 'medium'
 * @param {string} shape - 按钮形状：'circle' | 'round' | 'square'，默认为 'square'
 * @param {string} status - 按钮状态：'success' | 'warning' | 'danger' | 'normal'，默认为 'normal'
 * @param {boolean} loading - 是否加载中，默认为 false
 * @param {boolean} disabled - 是否禁用，默认为 false
 * @param {boolean} long - 是否撑满容器，默认为 false
 * @param {string} htmlType - HTML 按钮类型：'button' | 'submit' | 'reset'，默认为 'button'
 * @param {string} customClass - 自定义类名，默认为空字符串
 * @emits click - 点击事件
 * @slot prefix - 前置图标插槽
 * @slot suffix - 后置图标插槽
 * @slot icon - 兼容旧版图标插槽
 */

import { computed, useAttrs } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'primary',
    validator: (value) => ['default', 'primary', 'secondary', 'dashed', 'outline'].includes(value),
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['mini', 'small', 'medium', 'large'].includes(value),
  },
  shape: {
    type: String,
    default: 'square',
    validator: (value) => ['circle', 'round', 'square'].includes(value),
  },
  status: {
    type: String,
    default: 'normal',
    validator: (value) => ['success', 'warning', 'danger', 'normal'].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  long: {
    type: Boolean,
    default: false,
  },
  htmlType: {
    type: String,
    default: 'button',
    validator: (value) => ['button', 'submit', 'reset'].includes(value),
  },
  customClass: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['click'])

const attrs = useAttrs()

/**
 * @description 计算传递给 a-button 的 props
 */
const buttonProps = computed(() => {
  const { customClass, ...restProps } = props

  return {
    ...restProps,
    disabled: props.disabled, // loading 时不设为 disabled，由样式控制
    loading: props.loading,
    ...attrs,
  }
})

/**
 * @description 处理点击事件
 */
const handleClick = (e) => {
  if (!props.disabled && !props.loading) {
    emit('click', e)
  }
}
</script>

<style lang="scss" scoped>
.button {
  transition: all 0.3s ease;
  box-sizing: border-box;
  margin: 0; // 统一初始 margin
  font-weight: 600; // 统一初始字体粗细
  padding: 8px 16px;
  width: fit-content;

  :deep(.arco-btn-content) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__content {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__suffix {
    display: inline-flex;
    align-items: center;
  }

  &--loading {
    cursor: default;
    opacity: 0.7 !important; // 强制使用透明度，保留背景色
    pointer-events: none; // 防止重复点击
  }

  // 针对不同类型的 Loading 样式覆盖，防止被 disabled 样式冲掉颜色
  &--loading-primary,
  &--loading-status-normal,
  &--loading-status-danger {
    background: $primary-color !important;
    border-color: $primary-color !important;
    color: $bg-white !important;
  }

  &--loading-status-success {
    background: $success-color !important;
    border-color: $success-color !important;
    color: $bg-white !important;
  }

  &--loading-status-warning {
    background: $auxiliary-color-3 !important;
    border-color: $auxiliary-color-3 !important;
    color: $bg-white !important;
  }

  &--loading-secondary {
    background: $secondary-color !important;
    border-color: $secondary-color !important;
    color: $bg-white !important;
  }

  &--loading-outline {
    background: transparent !important;
    border-color: $primary-color !important;
    color: $primary-color !important;
  }

  &--type-primary {
    // 仅主要按钮保留加粗，以示区分，若需完全一致可改为 400
    font-weight: 600;
  }

  &--long {
    width: 100%;
  }

  &--size-mini {
    height: 24px;
    font-size: 12px;
    padding: 0 12px;
    font-weight: 400;
  }

  &--size-small {
    height: 32px;
    font-size: 14px;
    padding: 0 20px;
    font-weight: 400;
  }

  &--size-medium {
    height: auto;
    font-size: 14px;
    padding: 8px 16px;
    font-weight: 400;

    @include respond-to-max('lg') {
      padding: 7px 12px;
      font-size: 14px;
    }
  }

  &--size-large {
    height: 48px;
    font-size: 16px;
    padding: 0 32px;
    font-weight: 600;

    @include respond-to-max('lg') {
      height: 40px;
      font-size: 14px;
      padding: 0 24px;
    }
  }

  &--shape-round {
    border-radius: 22px;
  }

  &--shape-circle {
    border-radius: 50%;
  }

  &--shape-square {
    border-radius: 6px;
  }

  &--type-primary {
    background: $primary-color;
    border: 1px solid $primary-color;
    color: $bg-white;

    &:hover:not(:disabled):not(.button--loading) {
      background: $primary-color;
      opacity: 0.8;
      border: 1px solid $primary-color;
    }

    &:active:not(:disabled):not(.button--loading) {
      background: $primary-color;
      border: 1px solid $primary-color;
    }

    &:disabled:not(.button--loading) {
      background: #fdba8a;
      border: 1px solid #fdba8a;
      color: $bg-white;
      cursor: not-allowed;
      opacity: 1;
    }
  }

  &--type-primary.button--status-success {
    background: $success-color;
    border: 1px solid $success-color;

    &:hover:not(:disabled):not(.button--loading) {
      background: $success-color;
      opacity: 0.8;
      border: 1px solid $success-color;
    }
  }

  &--type-primary.button--status-warning {
    background: $auxiliary-color-3;
    border: 1px solid $auxiliary-color-3;

    &:hover:not(:disabled):not(.button--loading) {
      background: $auxiliary-color-3;
      opacity: 0.8;
      border: 1px solid $auxiliary-color-3;
    }
  }

  &--type-primary.button--status-danger {
    background: $primary-color;
    border: 1px solid $primary-color;
  }

  &--type-default {
    background: $bg-white;
    border: 1px solid rgba(0, 0, 0, 0.12);
    color: $text-title-color;
    box-shadow: none !important;

    &:hover:not(:disabled):not(.button--loading) {
      background: $bg-color;
      border-color: $border-hover-color;
      color: $text-title-color;
    }

    &:active:not(:disabled):not(.button--loading) {
      background: $bg-color;
      border-color: $border-hover-color;
      color: $text-title-color;
    }

    &:disabled:not(.button--loading) {
      background: $bg-white;
      border: 1px solid $border-dashed-color;
      color: $text-subtitle-color;
      cursor: not-allowed;
    }
  }

  &--type-secondary {
    background: $secondary-color;
    border: 1px solid $secondary-color;
    color: $bg-white;

    &:hover:not(:disabled):not(.button--loading) {
      background: $secondary-color !important;
      border-color: $secondary-color !important;
      color: $bg-white !important;
      opacity: 0.8;
    }

    &:active:not(:disabled):not(.button--loading) {
      background: $secondary-color !important;
      border-color: $secondary-color !important;
      color: $bg-white !important;
      opacity: 0.9;
    }

    &:disabled:not(.button--loading) {
      background: $border-dashed-color;
      border: 1px solid $border-dashed-color;
      cursor: not-allowed;
      opacity: 1;
    }
  }

  &--type-outline {
    background: transparent;
    border: 1px solid $primary-color;
    color: $primary-color;

    &:hover:not(:disabled):not(.button--loading) {
      background: rgba($primary-color, 0.05);
      border-color: $primary-color;
      color: $primary-color;
    }

    &:active:not(:disabled):not(.button--loading) {
      background: rgba($primary-color, 0.1);
      border-color: $primary-color;
      color: $primary-color;
    }

    &:disabled:not(.button--loading) {
      border: 1px solid $border-dashed-color;
      color: $text-subtitle-color;
      cursor: not-allowed;
    }
  }

  &--type-outline.button--status-success {
    border: 1px solid $success-color;
    color: $success-color;

    &:hover:not(:disabled):not(.button--loading) {
      background: rgba($success-color, 0.05);
      border-color: $success-color;
      color: $success-color;
    }
  }

  &--type-outline.button--status-warning {
    border: 1px solid $auxiliary-color-3;
    color: $auxiliary-color-3;

    &:hover:not(:disabled):not(.button--loading) {
      background: rgba($auxiliary-color-3, 0.05);
      border-color: $auxiliary-color-3;
      color: $auxiliary-color-3;
    }
  }

  &--type-outline.button--status-danger {
    border: 1px solid $primary-color;
    color: $primary-color;

    &:hover:not(:disabled):not(.button--loading) {
      background: rgba($primary-color, 0.05);
      border-color: $primary-color;
      color: $primary-color;
    }
  }

  &--type-dashed {
    background: $bg-white;
    border: 1px dashed $border-dashed-color;
    color: $text-title-color;

    &:hover:not(:disabled):not(.button--loading) {
      border: 1px dashed $primary-color;
      color: $primary-color;
      background: $bg-white;
    }

    &:active:not(:disabled):not(.button--loading) {
      border: 1px dashed $primary-color;
      color: $primary-color;
      background: $bg-white;
    }

    &:disabled:not(.button--loading) {
      border: 1px dashed $border-dashed-color;
      color: $text-subtitle-color;
      cursor: not-allowed;
    }
  }
}
</style>
